import type { ReactNode } from 'react';
import { confettiPieces, type FlowingComment } from './stage-helpers.js';

/** 紙吹雪の色（紺地に映える金・白・赤・水色・緑）。 @graph-connects none */
const CONFETTI_COLORS = ['#facc15', '#ffffff', '#f87171', '#38bdf8', '#4ade80'];

/**
 * ステージ画面の背景。紺のグラデーションに、ゆっくり流れる波と揺れる帆船（カティサーク号）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の背景（紺の海・流れる波・揺れる帆船）
 * @graph-connects none
 */
export function StageBackdrop(): ReactNode {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#1e3a8a_0%,#0b1533_55%,#020617_100%)]" />
      <svg
        className="cs-ship-bob absolute bottom-[16%] right-[6%] h-[22vh] w-auto opacity-25"
        viewBox="0 0 200 160"
        fill="#facc15"
      >
        {/* 3 本マストのクリッパー船のシルエット */}
        <path d="M20 120 L180 120 L160 145 L40 145 Z" />
        <rect x="58" y="20" width="3" height="100" />
        <rect x="98" y="8" width="3" height="112" />
        <rect x="138" y="24" width="3" height="96" />
        <path d="M62 28 Q86 48 62 70 Z M62 74 Q90 92 62 112 Z" />
        <path d="M102 14 Q130 38 102 62 Z M102 66 Q134 90 102 114 Z" />
        <path d="M142 30 Q166 50 142 72 Z M142 76 Q168 94 142 112 Z" />
        <path d="M180 120 L198 104 L196 108 L182 122 Z" />
      </svg>
      {[0, 1].map((layer) => (
        <div
          key={layer}
          className={`cs-wave absolute bottom-0 left-0 flex w-[200%] ${
            layer === 0 ? 'h-[14vh] opacity-40' : 'h-[9vh] opacity-60'
          }`}
        >
          {[0, 1].map((half) => (
            <svg
              key={half}
              className="h-full w-1/2"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill={layer === 0 ? '#1d4ed8' : '#1e40af'}
            >
              <path d="M0 60 Q150 0 300 60 T600 60 T900 60 T1200 60 V120 H0 Z" />
            </svg>
          ))}
        </div>
      ))}
    </div>
  );
}

/** 紙吹雪の props @graph-connects none */
interface ConfettiProps {
  /** 枚数。 */
  count?: number;
}

/**
 * 紙吹雪。依存ライブラリを増やさないよう、絶対配置の div に CSS アニメーションを当てる。
 * 配置は番号から決める（再描画で位置が飛ばないように）。動きを減らす設定では出さない（CSS 側）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の紙吹雪（優勝者の発表・表彰で出す）
 * @graph-connects confettiPieces [calls] 紙吹雪の配置
 */
export function Confetti({ count = 120 }: ConfettiProps): ReactNode {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      {confettiPieces(count).map((piece, i) => (
        <span
          key={i}
          className="cs-confetti absolute top-0 block h-3 w-2 rounded-sm"
          style={{
            left: `${String(piece.left)}%`,
            backgroundColor: CONFETTI_COLORS[piece.color],
            animationDelay: `${String(piece.delayMs)}ms`,
            animationDuration: `${String(piece.durationMs)}ms`,
            rotate: `${String(piece.rotate)}deg`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * 1 位のドラムロール中に、画面を暗転させて左右からスポットライトを振る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面で 1 位の発表前に暗転し、スポットライトを振る
 * @graph-connects none
 */
export function ChampionSpotlights(): ReactNode {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/70">
      <div className="cs-spot-left absolute bottom-0 left-[10%] h-[140vh] w-[30vw] origin-bottom bg-[linear-gradient(to_top,rgb(250_204_21/0.45),transparent)] [clip-path:polygon(45%_100%,55%_100%,100%_0,0_0)]" />
      <div className="cs-spot-right absolute bottom-0 right-[10%] h-[140vh] w-[30vw] origin-bottom bg-[linear-gradient(to_top,rgb(250_204_21/0.45),transparent)] [clip-path:polygon(45%_100%,55%_100%,100%_0,0_0)]" />
    </div>
  );
}

/**
 * 1 位の発表の瞬間に、画面全体を白く光らせて消す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面で 1 位の発表の瞬間に画面を白く光らせる
 * @graph-connects none
 */
export function ChampionFlash(): ReactNode {
  return <div aria-hidden="true" className="cs-flash absolute inset-0 z-20 bg-white" />;
}

/** 流れるコメントの props @graph-connects none */
interface CommentStreamProps {
  comments: FlowingComment[];
}

/**
 * 応援コメントを右から左へ流す（投票者は伏せ、宛先だけ添える）。段ごとに縦位置を変えて重ならないようにする。
 * 背景のぼかし（backdrop-filter）は 1 枚ごとに再合成が走り、数十件流すと画面が固まるので使わない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面で応援コメントを匿名で画面に流す
 * @graph-connects none
 */
export function CommentStream({ comments }: CommentStreamProps): ReactNode {
  if (comments.length === 0) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      {comments.map((c) => (
        <div
          key={c.key}
          className="cs-comment absolute left-0 whitespace-nowrap rounded-full bg-black/60 px-5 py-2 text-[clamp(1rem,2.2vw,1.8rem)] font-semibold text-white shadow-lg"
          style={{
            top: `${String(8 + c.lane * 13)}%`,
            animationDelay: `${String(c.delayMs)}ms`,
          }}
        >
          <span className="mr-3 text-amber-300">→ {c.to}</span>
          {c.text}
        </div>
      ))}
    </div>
  );
}
